import { createSelector, createSlice } from '@reduxjs/toolkit'
import { normalizeExtrasToAttractions, normalizeCartTour, getTourPricingBreakdown } from '../../utils/checkout'

const initialState = {
  checkout: {
    tours: [],
    travelers: {
      mainPasseger: {},
      others: []
    }
  }
}

const mergeTourForUpsert = (current = {}, incoming = {}) => {
  const incomingNormalized = normalizeCartTour(incoming || {});
  return normalizeCartTour({
    ...current,
    ...incomingNormalized,
    options: {
      ...(current.options || {}),
      ...(incomingNormalized.options || {}),
    },
    customizations: {
      ...(current.customizations || {}),
      ...(incomingNormalized.customizations || {}),
    },
  });
};

export const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    saveMainPassenger: (state, action) => {
      state.checkout.travelers.mainPasseger = action.payload;
    },
    add: () => {
      // deprecated
    },
    addTour: (state, action) => {
      const incoming = normalizeCartTour(action.payload || {});
      if (!incoming?.slug) return;

      const idx = state.checkout.tours.findIndex((tour) => tour.slug === incoming.slug);
      if (idx >= 0) {
        state.checkout.tours[idx] = mergeTourForUpsert(state.checkout.tours[idx], incoming);
      } else {
        state.checkout.tours.push(incoming);
      }
    },
    remove: (state, action) => {
      state.checkout.tours = state.checkout.tours.filter(tour => tour.slug !== action.payload)
    },
    setAssitants: () => {
      // deprecated
    },
    removeExtraActivities: (state, action) => {
      const { url } = action.payload || {};
      const idx = state.checkout.tours.findIndex((tour) => tour.slug === url);
      if (idx < 0) return;

      const current = state.checkout.tours[idx] || {};
      const selectedAttractions = normalizeExtrasToAttractions(action.payload?.extras || {});
      state.checkout.tours[idx] = normalizeCartTour({
        ...current,
        options: {
          ...(current.options || {}),
          selectedAttractions,
        },
        customizations: {
          ...(current.customizations || {}),
          attractions: selectedAttractions,
        },
      });
    },
    setExtraActivities: (state, action) => {
      const { url, tourData } = action.payload || {};
      const selectedAttractions = normalizeExtrasToAttractions(action.payload?.extras || {});
      const idx = state.checkout.tours.findIndex((tour) => tour.slug === url);

      if (idx >= 0) {
        const current = state.checkout.tours[idx] || {};
        state.checkout.tours[idx] = normalizeCartTour({
          ...current,
          ...(tourData || {}),
          options: {
            ...(current.options || {}),
            ...((tourData && tourData.options) || {}),
            selectedAttractions,
          },
          customizations: {
            ...(current.customizations || {}),
            ...((tourData && tourData.customizations) || {}),
            attractions: selectedAttractions,
          },
        });
        return;
      }

      if (tourData) {
        state.checkout.tours.push(normalizeCartTour({
          ...tourData,
          options: {
            ...(tourData.options || {}),
            selectedAttractions,
          },
          customizations: {
            ...(tourData.customizations || {}),
            attractions: selectedAttractions,
          },
        }));
      }
    },
    emptyCart: (state) => {
      state.checkout.tours = [];
      state.checkout.travelers.mainPasseger = {};
    }
  },
})

export const { add, remove, addTour, setAssitants, saveMainPassenger, emptyCart, setExtraActivities, removeExtraActivities } = cartSlice.actions

export const selectTours = (state) => state?.cart?.checkout?.tours || [];

export const totalPrice = (state) => state.cart.checkout.tours.reduce((acc, item) => acc + (Number(item?.total) || 0), 0);
export const selectCartItems = (state) => state?.cart?.checkout?.tours;

export const selectTotalPrice = createSelector(
  [selectCartItems],
  (items) => (items || []).reduce((total, item) => {
    try {
      const breakdown = getTourPricingBreakdown(item);
      return total + (Number(breakdown?.totals?.total) || 0);
    } catch (e) {
      return total;
    }
  }, 0)
);

export const selectSubtotalPrice = createSelector(
  [selectCartItems],
  (items) => (items || []).reduce((total, item) => {
    try {
      const breakdown = getTourPricingBreakdown(item);
      return total + (Number(breakdown?.totals?.subtotal) || 0);
    } catch (e) {
      return total;
    }
  }, 0)
);


export const selectProductTotal = createSelector(
  [selectCartItems, (state, name) => name],
  (items, name) => {
    const product = (items || []).find((item) => item.name === name);
    if (!product) return 0;
    try {
      const breakdown = getTourPricingBreakdown(product);
      return Number(breakdown?.totals?.total) || 0;
    } catch (e) {
      return 0;
    }
  }
);

export const selectProductSubtotal = createSelector(
  [selectCartItems, (state, name) => name],
  (items, name) => {
    const product = (items || []).find((item) => item.name === name);
    if (!product) return 0;
    try {
      const breakdown = getTourPricingBreakdown(product);
      return Number(breakdown?.totals?.subtotal) || 0;
    } catch (e) {
      return 0;
    }
  }
);

export const selectAssitants = (state) => state?.cart?.checkout?.travelers?.others || [];

export default cartSlice.reducer
